import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, NavLink } from 'react-router-dom';
import { useRateMutation, useBookmarkMutation } from '../services/recipes';
import { Box, Stack, Typography, Rating as MuiRating, IconButton, SpeedDial, SpeedDialAction, Card as MuiCard, CardContent, Chip, ButtonBase, Skeleton, Avatar, Snackbar, Link } from '@mui/material';
import { LoadingButton as Button } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { FacebookShareButton, WhatsappShareButton } from "react-share";
import { useAuth } from '../hooks/auth';
import { useLayout } from '../hooks/layout';
import { setImagevariant, readableTime } from '../utils/helpers';
import { translationsMaps } from '../utils/maps';
import { StarIcon, ShareIcon, FacebookIcon, BookmarkIcon, BookmarkFilledIcon, ChatIcon, ClockOutlineIcon, PlayIcon } from './Icons';
import LinkIcon from '@mui/icons-material/Link';
import { SocialIcon } from 'react-social-icons';
import { Lottie } from './Loading';

export const Chat = (props) => {

	const { t } = useTranslation();

	return (
		<Stack
			direction="row"
			alignItems="center"
		>
			<Typography
				component="div"
				variant="caption"
				color="textPrimary"
			>
				{t("recipeDetails_chef_chatButtonTitle")}
			</Typography>
			<Box>
				<IconButton
					color="dark"
					component={Link}
					href={props.link}
					target="_blank"
				>
					<ChatIcon />
				</IconButton>
			</Box>
	    </Stack>
	);
}

export const Share = (props) => {

	const {url} =  props;
	const [copied, setCopied] = React.useState(false);

	return (
		<Box
			sx={{
				position: 'relative',
				width: 56,
				height: 56
			}}
		>
			<SpeedDial
				ariaLabel="Share url"
		        icon={<ShareIcon color="dark" />}
		        sx={{
		        	position: 'absolute',
		        	bottom: 0,
		        	left: 0
		        }}
		        FabProps={{
		        	sx: {
		        		background: 'transparent',
		        		boxShadow: 'none',
		        		'&:hover': {
		        			background: 'transparent'
		        		}
		        	}
		        }}
		    >
	            <SpeedDialAction
	            	FabProps={{
	            		component: FacebookShareButton,
	            		url: url,
	            		color: 'white',
	            		style: {
	            			background: 'white'
	            		}
	            	}}
	           		icon={
	           			<FacebookIcon />
	           		}
	            	tooltipTitle="Facebook"
	            />
	            <SpeedDialAction
	            	FabProps={{
	            		component: WhatsappShareButton,
	            		url: url,
	            		color: 'white',
	            		style: {
	            			background: 'white'
	            		}
	            	}}
	           		icon={
	           			<SocialIcon round="true" network="whatsapp" style={{width: 30, height: 30}} />
	           		}
	            	tooltipTitle="WhatsApp"
	            />
	            <SpeedDialAction
	            	FabProps={{
	            		//component: WhatsappShareButton,
	            		//url: url,
	            		color: 'white',
	            		style: {
	            			background: 'white'
	            		}
	            	}}
	            	onClick={() => {
	            		navigator.clipboard.writeText(window.location.href).then(() => {
							      setCopied(true);
							    });
	            	}}
	           		icon={
	           			<LinkIcon color="dark" fontSize="small" />
	           		}
	            	tooltipTitle="Copia link ricetta"
	            />
		    </SpeedDial>
		    <Snackbar
	        open={copied}
	        autoHideDuration={4000}
	        onClose={() => {setCopied(false)}}
	        message="Il link è stato copiato negli appunti!"
	      />
	    </Box>
	);
}

export const Bookmark = (props) => {

	const { id, saved, refetch } =  props;
	const [bookmark, { isSuccess, isLoading }] = useBookmarkMutation();

	React.useEffect(() => {
		if(isSuccess) {
			refetch();
		}
	}, [isSuccess])

	return (
		<IconButton
      component={Button}
      loading={isLoading}
			color={saved ? 'primary' : 'dark'}
			onClick={() => bookmark({id: id, value: !saved})}
		>
			{
				saved ? 
				<BookmarkFilledIcon /> :
				<BookmarkIcon />
			}
		</IconButton>
	);
}

const Highlight = styled(Box)(({ theme }) => ({
  width: '33.3%',
  backgroundColor: '#ccc',
  color: 'white',
  textAlign: 'center',
  padding: theme.spacing(1),
  '&.duration': {
  	backgroundColor: theme.palette.info.main
  },
  '&.rating': {
  	backgroundColor: theme.palette.yellow.main
  },
  '& .MuiTypography-caption': {
  	fontSize: '0.75rem'
  },
  '& .MuiTypography-root': {
  	lineHeight: 0.9
  }
}));

export const Highlights = (props) => {

	const { difficulty, duration, rating, hasRated } = props;
	const { t } = useTranslation();
	const {theme} = useLayout();
	
	return(
		<Stack
			direction="row"
		>
			<Highlight
				sx={{
					backgroundColor: theme.palette[difficulty].main
				}}
			>
				<Typography sx={{textTransform: 'capitalize'}}>
					<b>{t(translationsMaps.difficulty[difficulty])}</b>
				</Typography>
				<Typography variant="caption">
					{t("recipeDetails_info_difficultyHeader")}
				</Typography>
			</Highlight>
			<Highlight
				className="duration"
			>
				<Typography>
					<b>{ readableTime(duration, 'minutes') }</b>
				</Typography>
				<Typography variant="caption">
					{t("recipeDetails_info_totalTimeHeader")}
				</Typography>
			</Highlight>
			<Highlight
				className="rating"
			>
				{
					!hasRated ?
					<Typography 
						component={Box}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
					> 
						<Box mr={1}><b>{ rating.average || 0 }</b></Box>
						<StarIcon color="white" sx={{fontSize: '0.9rem'}} />
					</Typography> :
					<Typography 
						component={Box}
						sx={{
							mt: -1,
							display: 'flex',
							alignItems: 'flex-end',
							justifyContent: 'center'
						}}
					> 
						<Box><b>{t("recipeDetails_info_ratedHeader")}</b></Box>
						<Box
							sx={{
								width: '1.3em',
								height: '1.3em'
							}}
						>
							<Lottie animation="rated" />
						</Box>
					</Typography>
				}
				<Typography variant="caption">
					{t("recipeDetails_info_ratingHeader")}
				</Typography>
			</Highlight>
		</Stack>
	)
}

const Icon = styled(Box)(({ theme }) => ({
  width: '1em',
  height: '1em',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#E3E3E3',
  borderRadius: 15,
  [theme.breakpoints.down('md')]: {
  	width: 30,
  	height: 30,
  	borderRadius: 10,
  },
  '&.checked': {
    backgroundColor: theme.palette.yellow.main,
  }
}));

const StyledRating = styled(MuiRating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.yellow.main,
  },
  '& .MuiRating-iconHover': {
    color: theme.palette.yellow.main,
  },
  '& label': {
    padding: '0 5px'
  }
}));

export const Rating = (props) => {

  	const { id, rating, onNewValue } = props;
  	const { auth } = useAuth();
  	const [value, setValue] = React.useState(0);
  	const [rate, { isSuccess, isLoading }] = useRateMutation();

  	React.useEffect(() => {
  		setValue(rating.user_rated ? rating.user_rating : 0);
  	}, [id])

  	return (
      <StyledRating
      	disabled={rating.user_rated || isLoading}
        value={value}
        onChange={ async (e, newValue) => {
        	setValue(newValue);
        	await rate({
        		id: id,
        		value: newValue 
        	});
        	if(onNewValue) {
	    		onNewValue(value);
	    	}
        }}
        icon={
          <Icon className="checked">
            <StarIcon color="white" sx={{fontSize: '1rem'}} />
          </Icon>
        }
        emptyIcon={
          <Icon>
            <StarIcon color="white" sx={{fontSize: '1rem'}} />
          </Icon>
        }
      />
  	);
}

const LineClampText = styled(Typography)(({ theme }) => ({
	display: '-webkit-box',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 3,
	overflow: 'hidden',
}));

export const LineClamp = (props) => <LineClampText {...props} />;

const CardMedia = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  backgroundColor: 'rgba(0, 0, 0, 0.11)',
  position: 'relative'
}));

const CardMediaContent = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0
}));

const CardMediaPayoff = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10
}));

export const Card = (props) => {

  const { recipe, skeleton, width, responsive, bigSize } = props;
  const { t } = useTranslation();
  const { isMobile } = useLayout();
  let buttonProps = {};
  if(recipe) {
  	buttonProps = {
  		component: NavLink,
  		to: generatePath('/ricette/:slug', {slug: recipe.slug})
  	}
  }

  return (
  	<Box>
	  	<ButtonBase 
  			{...buttonProps}
  			sx={{
  				display: 'block',
  				width: '100%'
  			}}
	  	>
		    <MuiCard 
		      sx={{
		      	borderRadius: 0,
		        //minWidth: width ? width : bigSize ? 375 : 272,
		        minWidth: width ? width : {
		        	xs: bigSize ? 300 : 272,
		        	md: bigSize ? 340 : 272
		        },
		        '&:hover': {
		        	opacity: 0.8
		        }
		      }}
		    >	
		    	<Box
		    		sx={{ 
		    			display: 'flex', 
		    			flexDirection: {
		    				xs: responsive ? 'row' : 'column',
		    				md: 'column'
		    			} 
		    		}}
		    	>
		    		<CardMedia
			        sx={{
			          backgroundImage: recipe ? 'url('+setImagevariant(recipe.thumbnail.url, 400)+')' : '',
			          maxWidth: {
			          	xs: responsive ? '30vw' : 'none',
			          	md: 'none'
			          },
			          paddingTop: {
			          	xs: responsive ? '40%' : '105%',
			          	md: '105%'
			          }
			        }}
			      >
			      	{
			      		(!responsive && Boolean(recipe) && Boolean(recipe.label)) &&
			      		<CardMediaPayoff>
				        	<Chip 
				        		label={recipe.label.text} 
				        		size="small"
				        		color="primary"
				        		sx={{
				        			py: 1.7,
				        			px: .5,
				        			backgroundColor: Boolean(recipe.label.color_bg) ? '#' + recipe.label.color_bg : '',
				        			color: Boolean(recipe.label.color_fg) ? '#' + recipe.label.color_fg : ''
				        		}} 
				        	/>
				        </CardMediaPayoff>
			      	}
			      	{
			      		(bigSize && recipe.has_video) &&
			      		<Box
			      			sx={{
			      				color: (theme) => theme.palette.white.main,
			      				position: 'absolute',
			      				top: 0,
			      				left: 0,
			      				width: '100%',
			      				height: '100%',
			      				display: 'flex',
					          flexDirection: 'column',
					          justifyContent: 'center',
					          alignItems: 'center'
			      			}}
			      		>
									<PlayIcon color="inherit" fontSize="large" />
							  </Box>
			      	}
			      </CardMedia> 
			      <Box 
			      	sx={{
			      		p:2, 
			      		pb: 1, 
			      		width: '100%',
			      		boxSizing: 'border-box',
			      		display: 'flex',
			      		flexDirection: 'column',
			      		justifyContent: 'space-between'
			      	}}>
			      	<Box>
				      	{
				      		recipe ?
				      		<>
				      			<Typography
				      				component="div"
						      		color="primary"
						      		variant="caption"
						      		gutterBottom
						      	>
						      		<b>{ recipe.category }</b>
						      	</Typography>
						      	<Box
						      		sx={{
						      			height: {
						      				xs: bigSize ? 52 : 47,
						      				md: bigSize ? 65 : 55
						      			}
						      		}}
						      	>
							        <LineClamp
							        	gutterBottom 
							        	color="text.primary"
							        	variant={bigSize ? "h4" : "body1"} 
							        	sx={{
							        		lineHeight: 1,
							        		WebkitLineClamp: 2
							        	}}
							        >
							          <b>{ recipe.title }</b>
							        </LineClamp>
						        </Box>
						        <Box
						        	height={bigSize ? 35 : 25}
						        >
						        	{
							        	Boolean(recipe.chef) &&
							        	<>
							        		{
									        	bigSize ?
									        	<Box
									        		sx={{
									        			display: 'flex',
									        			alignItems: 'center'
									        		}}
									        	>
									        		<Avatar 
																src={setImagevariant(recipe.chef.image.url, 200)} 
																sx={{
																	width: 30,
																	height: 30,
																	mr: 1,
																	boxShadow: '0px 3px 5px #00000033'
																}}
															/>
															<Typography component="div" sx={{fontWeight: 500, pt: .5, fontSize: '1rem !important'}}>{recipe.chef.name}</Typography>
									        	</Box> :
									        	<Typography
										        	component="div"
										      		color="textPrimary"
										      		variant="caption"
										      		gutterBottom
										      	>
										      		{recipe.chef.name}
										      	</Typography>	
									        }	
							        	</>
							        }
						        </Box>
				      		</>:
				      		<>
				      			<Skeleton animation="wave" height={16} width="30%" style={{ marginBottom: 6 }} />
				      			<Skeleton animation="wave" height={24} style={{ marginBottom: 3 }} />
		            		<Skeleton animation="wave" height={24} width="40%" style={{ marginBottom: 6 }} />
		            		<Skeleton animation="wave" height={16} width="30%" style={{ marginBottom: 12 }} />
				      		</>
				      	}
			      	</Box>
			      	{
			      		!bigSize &&
			      		<Box>
				      		<Stack
					      		direction="row"
					      		justifyContent="space-between"
					      		alignItems="center"
					      	>
					      		<Box sx={{width: '33.3%'}}>
					      			{
					      				Boolean(recipe) ?
					      				<Typography
						      				variant="caption"
						      				component="div"
						      				color="text.secondary"
						      				sx={{
						      					textTransform: 'capitalize',
						      					fontSize: '0.9rem'
						      				}}
						      			>
						      				{t(translationsMaps.difficulty[recipe.difficulty])}
						      			</Typography> :
						      			<Skeleton animation="wave" height={14} width="50%" sx={{marginRight: 'auto'}} />
					      			}
					      		</Box>
					      		<Box sx={{width: '33.3%'}}>
					      			{
					      				Boolean(recipe) ?
					      				<Typography
						      				align="center"
						      				variant="caption"
						      				component="div"
						      				color="text.secondary"
						      				sx={{
						      					fontSize: '0.9rem'
						      				}}
						      			>
						      				<ClockOutlineIcon fontSize="inherit" sx={{verticalAlign: 'text-bottom'}} />&nbsp;
						      				{ readableTime(recipe.times_total, 'minutes') }
						      			</Typography> :
						      			<Skeleton animation="wave" height={14} width="50%" sx={{margin: 'auto'}} />
					      			}
					      		</Box>
					      		<Box sx={{width: '33.3%'}}>
					      			{
					      				Boolean(recipe) ?
					      				<Typography
					      					align="right"
						      				variant="caption"
						      				component="div"
						      				color="text.secondary"
						      				sx={{
						      					fontSize: '0.9rem'
						      				}}
						      			>
						      				<StarIcon fontSize="inherit" color="yellow" sx={{verticalAlign: 'text-bottom'}} />&nbsp;
						      				{ recipe.rating || 0 }
						      			</Typography> :
					      				<Skeleton animation="wave" height={14} width="50%" sx={{marginLeft: 'auto'}} />
					      			}
					      		</Box>
					      	</Stack>
				      	</Box>
			      	}
			      </Box>
			      {
			      	bigSize &&
			      	<Highlights 
								difficulty={recipe.difficulty}
								duration={recipe.times_total}
								rating={{average: recipe.rating}}
							/>
			      }
		    	</Box>
		    </MuiCard>
	    </ButtonBase>
    </Box>
  );
}